import React from "react";
import ExpandableParagraph from "components/componentLibrary/content/ExpandableParagraph";
import Tooltip from "components/Tooltip";

import styles from "./styles.module.scss";

interface IProps {
  description: string;
  hasTurnstile: boolean;
  hasRoundTheClockAccess: boolean;
  hasAttendedLobby: boolean;
  hasVirtualTour: boolean;
  details: {
    "Wired Certification"?: string;
    "Walk Score®"?: number;
    "Noise Index"?: number;
    Floors?: number;
  };
  address: string;
  fullAddress: string;
  addresses: { address: string }[];
  name: string;
}

const AboutTheBuilding = ({
  details,
  description,
  hasTurnstile,
  hasVirtualTour,
  hasAttendedLobby,
  hasRoundTheClockAccess,
  address,
  fullAddress,
  addresses,
  name,
}: IProps) => {
  const wiredCert = details["Wired Certification"];
  const walkScore = details["Walk Score®"];
  const noiseIndex = details["Noise Index"];
  const floors = details.Floors;

  const filteredAddresses = addresses.filter((a) => a.address !== address);

  return (
    <>
      <p className={`bodyEmphasis ${styles.title}`}>About the Building</p>
      {description && (
        <>
          <p className={styles.subHeading}>Building Description</p>
          <ExpandableParagraph>
            <span
              // ensures dangerouslySetInnerHTML always renders correctly
              key={`${Math.random()} - ${Math.random()}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </ExpandableParagraph>
        </>
      )}
      <div>
        <p className={styles.subHeading}>Building Amenities</p>
        <div className={styles.buildingDetails}>
          {hasTurnstile && (
            <div className={styles.detail}>
              <i className="icon icon-check" /> Turnstile
            </div>
          )}
          {hasRoundTheClockAccess && (
            <div className={styles.detail}>
              <i className="icon icon-check" /> 24/7 Access
            </div>
          )}
          {hasAttendedLobby && (
            <div className={styles.detail}>
              <i className="icon icon-check" /> Attended Lobby
            </div>
          )}
          {hasVirtualTour && (
            <div className={styles.detail}>
              <i className="icon icon-check" /> Virtual Tour Available
            </div>
          )}
          {wiredCert && (
            <div className={styles.detail}>
              Wired Certification: {wiredCert}
              <Tooltip
                className={styles.buildingDetailTooltip}
                overlay={<p>A measure of internet connectivity quality</p>}
              >
                <span className={styles.buildingDetailExplanation}>
                  <i className="icon icon-help" />
                </span>
              </Tooltip>
            </div>
          )}
          {walkScore && (
            <div className={styles.detail}>
              Walk Score: {walkScore}
              <Tooltip
                className={styles.buildingDetailTooltip}
                overlay={<p>A score which represents proximity to amenities</p>}
              >
                <span className={styles.buildingDetailExplanation}>
                  <i className="icon icon-help" />
                </span>
              </Tooltip>
            </div>
          )}
          {noiseIndex && (
            <div className={styles.detail}>
              Noise Index: {noiseIndex}
              <Tooltip
                className={styles.buildingDetailTooltip}
                overlay={<p>A score representing noise pollution</p>}
              >
                <span className={styles.buildingDetailExplanation}>
                  <i className="icon icon-help" />
                </span>
              </Tooltip>
            </div>
          )}
          {floors && floors > 0 && (
            <div className={styles.detail}>{floors} Floors</div>
          )}
        </div>
      </div>
      {fullAddress && (
        <div>
          <p className={styles.subHeading}>Address</p>
          <address>
            {name && <p>{name}</p>}
            <p>{fullAddress}</p>
          </address>
        </div>
      )}
      {!!filteredAddresses?.length && (
        <div>
          <p className={styles.subHeading}>Alternative Addresses</p>
          <address>
            {filteredAddresses.map((a, index) => (
              <span key={a.address}>
                {a.address} {index !== filteredAddresses.length - 1 && ", "}
              </span>
            ))}
          </address>
        </div>
      )}
    </>
  );
};

export default AboutTheBuilding;
